import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Target, Users, Lightbulb, Sparkles, Infinity } from 'lucide-react';

const expertiseData = [
  {
    id: 1,
    slug: "bilincalti-programlama",
    title: "Bilinçaltı",
    titleLine2: "Programlama",
    subtitle: "Zihinsel Yeniden Doğuş",
    description: "Zihnin iç yapısını yeniden inşa ederek iç huzur, netlik ve kalıcı özgüven yaratırız. Kök inançları zarifçe dönüştürüp duygusal zekayı merkezine alırız.",
    icon: Brain,
    gradient: "from-purple-500 via-violet-500 to-purple-600",
    textGradient: "from-purple-400 to-violet-400",
    glowColor: "rgba(139, 92, 246, 0.6)",
  },
  {
    id: 2,
    slug: "noro-pazarlama",
    title: "Nöro",
    titleLine2: "Pazarlama",
    subtitle: "Markanın Kalp Atışı",
    description: "Tüketici zihnindeki 'satın alma' anını tetikleyen duyusal tetikleyicileri kullanarak markanın sadece akılda değil, kalpte yer etmesini sağlarız.",
    icon: Target,
    gradient: "from-pink-500 via-rose-500 to-pink-600",
    textGradient: "from-pink-400 to-rose-400",
    glowColor: "rgba(236, 72, 153, 0.6)",
  },
  {
    id: 3,
    slug: "ikna-teknikleri",
    title: "İkna",
    titleLine2: "Teknikleri",
    subtitle: "Sözün Büyüsü",
    description: "Karşınızdaki kişinin iç dünyasına uyum sağlayan, güven ve saygı çerçevesinde doğal liderlik oluşturan, en zorlu iletişimleri ustalıkla yönetme becerisi.",
    icon: Users,
    gradient: "from-cyan-500 via-blue-500 to-cyan-600",
    textGradient: "from-cyan-400 to-blue-400",
    glowColor: "rgba(6, 182, 212, 0.6)",
  },
  {
    id: 4,
    slug: "is-stratejileri",
    title: "İş",
    titleLine2: "Stratejileri",
    subtitle: "Vizyoner İnşa",
    description: "Yüksek fayda getiren, kullanıcı deneyimine odaklı çözümlerle iş fikrine ruh katmak ve teknolojik gelişimi kurumsal kimliğin zarif uzantısı haline getirmek.",
    icon: Lightbulb,
    gradient: "from-amber-500 via-orange-500 to-amber-600",
    textGradient: "from-amber-400 to-orange-400",
    glowColor: "rgba(245, 158, 11, 0.6)",
  },
  {
    id: 5,
    slug: "zihinsel-donusum",
    title: "Zihinsel",
    titleLine2: "Dönüşüm",
    subtitle: "Davranışsal Zarafet",
    description: "Kendi zihinsel kodlarını keşfederek kaosu bırakıp amaç odaklı düzeni benimseme süreci. Kişinin en saf potansiyelini hatırlama deneyimi sunarız.",
    icon: Sparkles,
    gradient: "from-emerald-500 via-teal-500 to-emerald-600",
    textGradient: "from-emerald-400 to-teal-400",
    glowColor: "rgba(16, 185, 129, 0.6)",
  },
  {
    id: 6,
    slug: "derin-ogrenme",
    title: "Derin",
    titleLine2: "Öğrenme",
    subtitle: "İçsel Keşif",
    description: "Zihinsel gürültüyü susturarak yaratıcılığın ve sezginin görünmez kaynaklarına erişim. Bireyin en yüksek performansı ile zahmetsizce buluşmasını sağlarız.",
    icon: Infinity,
    gradient: "from-indigo-500 via-purple-500 to-indigo-600",
    textGradient: "from-indigo-400 to-purple-400",
    glowColor: "rgba(99, 102, 241, 0.6)",
  },
];

const ExpertiseCard = ({ item, index }) => {
  return (
    <Link to={`/uzmanlik/${item.slug}`}>
      <motion.div
        className="relative group cursor-pointer"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-50px" }}
        transition={{ delay: index * 0.1, duration: 0.5 }}
      >
      {/* Glowing border effect */}
      <div 
        className="absolute -inset-[2px] rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-500"
        style={{ 
          background: `linear-gradient(135deg, ${item.glowColor}, transparent 40%, transparent 60%, ${item.glowColor})`,
          filter: 'blur(4px)'
        }}
      ></div>
      
      {/* Inner glow */}
      <div 
        className="absolute -inset-[1px] rounded-3xl opacity-0 group-hover:opacity-100 transition-all duration-500"
        style={{ 
          background: `linear-gradient(135deg, ${item.glowColor}, transparent 30%, transparent 70%, ${item.glowColor})`,
        }}
      ></div>
      
      {/* Card */}
      <motion.div 
        className="relative bg-gray-950/95 backdrop-blur-xl rounded-3xl p-6 border border-white/10 
                   group-hover:border-transparent transition-all duration-500 overflow-hidden h-full min-h-[340px] flex flex-col"
        whileHover={{ y: -8 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        {/* Background gradient on hover */}
        <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-[0.03] rounded-3xl transition-opacity duration-500`}></div>
        
        {/* Main Title - Large and Bold */}
        <div className="mb-2">
          <h3 className="text-white text-2xl sm:text-3xl font-black leading-none tracking-tight">
            {item.title}
          </h3>
          <h3 className={`text-2xl sm:text-3xl font-black leading-none tracking-tight bg-gradient-to-r ${item.textGradient} bg-clip-text text-transparent`}>
            {item.titleLine2}
          </h3>
        </div>
        
        {/* Subtitle */}
        <div className="mb-3">
          <p className="text-gray-400 text-sm font-medium">
            {item.subtitle}
          </p>
          <div className={`h-0.5 w-12 bg-gradient-to-r ${item.gradient} mt-1.5 rounded-full opacity-60`}></div>
        </div>
        
        {/* Description */}
        <p className="text-gray-500 text-sm leading-relaxed flex-grow">
          {item.description}
        </p>
      </motion.div>
      </motion.div>
    </Link>
  );
};

const ExpertiseAreas = () => {
  return (
    <section id="uzmanlik" className="relative py-24 bg-black overflow-hidden scroll-mt-20">
      {/* Background Glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-purple-600/10 rounded-full filter blur-[150px]"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-cyan-600/10 rounded-full filter blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-6 max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-block mb-6">
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 px-5 py-2 rounded-full">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span className="text-gray-300 text-sm font-light tracking-wider">Uzmanlık Alanları</span>
            </div>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 tracking-tight">
            Ustalığın{' '}
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
              6 Boyutu
            </span>
          </h2>
          
          <p className="text-gray-500 text-base font-light max-w-lg mx-auto">
            Bilinçaltından başlayan, hayatın her köşesine dokunan güç
          </p>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {expertiseData.map((item, index) => (
            <ExpertiseCard key={item.id} item={item} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExpertiseAreas;
